import { z } from "zod";
import {
  EMAIL_STATUSES_FORBIDDING_VERIFIED_AT,
  emailStatusSchema,
} from "../enums.js";
import {
  confidenceSchema,
  isoDateTimeSchema,
  nonEmptyStringSchema,
  uuidSchema,
} from "../primitives.js";

const nullableUrlSchema = z.union([z.string().url(), z.null()]);

const emailStatusesForbiddingVerifiedAt: ReadonlySet<string> = new Set(
  EMAIL_STATUSES_FORBIDDING_VERIFIED_AT,
);

const contactFields = {
  id: uuidSchema,
  hotel_id: uuidSchema,
  name: nonEmptyStringSchema,
  role_title: z.string().nullable(),
  email: z.union([z.string().email(), z.null()]),
  phone: z.string().nullable(),
  linkedin_url: nullableUrlSchema,
  source_url: nullableUrlSchema,
  confidence: confidenceSchema.nullable(),
  email_status: emailStatusSchema,
  verified_at: isoDateTimeSchema.nullable(),
  is_primary: z.boolean(),
  research_job_id: uuidSchema.nullable(),
  agent_run_id: uuidSchema.nullable(),
  created_at: isoDateTimeSchema,
  updated_at: isoDateTimeSchema,
} as const;

function refineContactEmailVerification(
  contact: {
    email_status: z.infer<typeof emailStatusSchema>;
    verified_at: string | null;
  },
  ctx: z.RefinementCtx,
) {
  if (
    emailStatusesForbiddingVerifiedAt.has(contact.email_status) &&
    contact.verified_at !== null
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${contact.email_status} emails cannot have verified_at set`,
      path: ["verified_at"],
    });
  }
}

/**
 * Canonical contact write/read schema.
 * Enforces inferred/invalid must not carry verified_at.
 */
export const contactSchema = z
  .object(contactFields)
  .superRefine(refineContactEmailVerification);

export type Contact = z.infer<typeof contactSchema>;

/** @deprecated Use contactSchema — kept as alias for clarity at call sites */
export const contactWriteSchema = contactSchema;
export const contactSchemaWithEmailRules = contactSchema;
